import { t } from '@glory/i18n';
import { ArrowLeft, Maximize2, X } from 'lucide-react';
import { motion, useIsPresent } from 'motion/react';
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { IconButton } from '../components/button.tsx';
import { Kbd, Shortcut } from '../components/kbd.tsx';
import { cn } from '../lib/cn.ts';
import { slideIn } from '../lib/motion.tsx';
import { hotkeyLabel, useHotkeys } from './hotkeys.ts';
import { useImmersive } from './immersive.ts';

export interface HybridPanelProps {
  title: ReactNode;
  /** Строка под заголовком: статус, метки. */
  meta?: ReactNode;
  /** Вкладки карточки. */
  tabs?: ReactNode;
  actions?: ReactNode;
  onClose: () => void;
  onPrevious?: (() => void) | undefined;
  onNext?: (() => void) | undefined;
  /** Развернуть на всю страницу (⌘↵). */
  onExpand?: (() => void) | undefined;
  children: ReactNode;
  className?: string;
}

/**
 * Поверх карточки открыт диалог, меню или всплывающее окно: клавиши принадлежат ему.
 * Иначе ↑↓ под диалогом «Новая ставка» пролистнули бы карточку на другого человека.
 */
const overlayOpen = () =>
  document.querySelector('[role="dialog"][data-state="open"], [role="menu"][data-state="open"]') !== null;
const noOverlay = () => !overlayOpen();

/**
 * Сколько панелей сейчас на экране. Соседняя запись (↑↓) часто монтирует новую панель вместо старой —
 * пока старая ещё открыта, новая не выезжает заново, а просто встаёт на место.
 */
let openPanels = 0;

const PANEL_MOTION = slideIn('right');

/**
 * Гибридная карточка: панель справа поверх списка. ↑↓ — соседние записи,
 * Esc — закрыть, ⌘↵ — та же карточка на всю страницу. На телефоне — на весь экран.
 *
 * Список под панелью не двигается: она лежит поверх (решение спецификации), поэтому раскладка не
 * перестраивается. Выезд и уход — Motion; чтобы панель уходила плавно, а не исчезала, место, где она
 * стоит, оборачивают в `<AnimatePresence initial={false}>` с постоянным `key`.
 *
 * Высота панели — окно под шапкой, а не страница: у длинного списка страница в несколько экранов,
 * и привязанная к ней панель росла бы вместе с ней — страница скроллилась бы, а внутренняя прокрутка
 * карточки не включалась. Поэтому на md+ панель `fixed` и прокручивается внутри.
 */
export function HybridPanel({
  title,
  meta,
  tabs,
  actions,
  onClose,
  onPrevious,
  onNext,
  onExpand,
  children,
  className,
}: HybridPanelProps) {
  const ref = useRef<HTMLElement>(null);
  const [enters] = useState(() => openPanels === 0);
  // Панель уходит (идёт анимация закрытия): клавиши и фокус ей уже не принадлежат — иначе ↓ сразу
  // после Esc открыл бы соседнюю запись под закрывающейся панелью.
  const present = useIsPresent();
  const live = () => present && noOverlay();
  useLayoutEffect(() => {
    openPanels += 1;
    return () => {
      openPanels -= 1;
    };
  }, []);
  // Телефон: карточка во весь экран — каркас не рисует шапку и нижнее меню.
  useImmersive(true);
  useHotkeys([
    { keys: 'esc', handler: onClose, when: live },
    { keys: 'mod+enter', handler: () => onExpand?.(), allowInInputs: true, when: live },
    { keys: 'up', handler: () => onPrevious?.(), when: live },
    { keys: 'down', handler: () => onNext?.(), when: live },
  ]);

  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);

  const panel = (
    <motion.aside
      ref={ref}
      tabIndex={-1}
      {...PANEL_MOTION}
      // Соседняя запись встаёт на место прежней панели без нового выезда.
      initial={enters ? PANEL_MOTION.initial : false}
      {...(present ? {} : { inert: true })}
      className={cn(
        'fixed inset-0 z-[var(--z-panel)] flex flex-col bg-surface outline-none',
        // На телефоне шапка приложения скрыта — берём её безопасные зоны сами.
        'top-[var(--call-bar-h)] pt-[var(--safe-top)] pb-[env(safe-area-inset-bottom)] md:pt-0 md:pb-0',
        'md:top-[var(--header-h)] md:right-0 md:bottom-0 md:left-auto md:w-[min(520px,48vw)] md:border-l-[1.5px] md:border-strong md:shadow-float',
        // Пока открыто окно, резерв полосы страницы снят, а `fixed` отступ `body` не двигает: держим край на месте.
        'md:mr-[var(--g-lock-inset,0px)]',
        className,
      )}
    >
      {/* Содержимое проявляется при каждой сборке панели: скелетон → карточка и переход к соседней записи
          не подменяют текст за один кадр. */}
      <header className="flex animate-fade-in flex-col gap-2.5 px-4 pt-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-title leading-tight font-bold">{title}</h2>
            {meta ? <div className="mt-1 flex flex-wrap gap-1.5">{meta}</div> : null}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <span className="mr-1 hidden items-center gap-1 lg:flex">
              <Kbd>{'↑↓'}</Kbd>
              {onExpand ? <Shortcut keys={hotkeyLabel('mod+enter')} /> : null}
              <Shortcut keys={hotkeyLabel('esc')} />
            </span>
            {actions}
            {onExpand ? (
              <IconButton
                className="hidden md:inline-flex"
                label={t('shell.expandCard')}
                icon={<Maximize2 />}
                onClick={onExpand}
              />
            ) : null}
            <IconButton label={t('shell.closeCard')} icon={<X />} onClick={onClose} />
          </div>
        </div>
        {tabs}
      </header>
      <div className="min-h-0 flex-1 animate-fade-in overflow-y-auto px-4 py-3">{children}</div>
    </motion.aside>
  );
  // Портал в `body`: иначе карточка попадала в stacking-контекст страницы (opacity-переход `<main>`
  // после клиентской навигации) и шапка с нижним меню рисовались поверх неё.
  if (typeof document === 'undefined') return panel;
  return createPortal(panel, document.body);
}

/** Та же карточка на всю страницу. */
export function CardPage({
  title,
  meta,
  tabs,
  actions,
  onBack,
  backLabel,
  children,
}: {
  title: ReactNode;
  meta?: ReactNode;
  tabs?: ReactNode;
  actions?: ReactNode;
  onBack: () => void;
  /** Куда ведёт «назад»: «Воронка», «Клиенты». Без подписи — «К списку». */
  backLabel?: string | undefined;
  children: ReactNode;
}) {
  useHotkeys([{ keys: 'esc', handler: onBack }]);
  return (
    <div className="flex animate-fade-in flex-col gap-3 px-4 py-4 md:px-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex w-fit items-center gap-1 text-small font-bold text-muted hover:text-text [&_svg]:size-4"
      >
        <ArrowLeft />
        {backLabel ?? t('shell.backToList')}
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-heading font-bold">{title}</h1>
          {meta ? <div className="mt-1.5 flex flex-wrap gap-1.5">{meta}</div> : null}
        </div>
        {actions ? <div className="flex gap-2">{actions}</div> : null}
      </div>
      {tabs}
      <div>{children}</div>
    </div>
  );
}
